import * as yup from 'yup'
import { TransactionAmountRule } from '@/entities/transactions/utils/enums'

export const NEGATIVE_RULES = [
  TransactionAmountRule.NEGATIVE,
  TransactionAmountRule.MAXZERO,
]
export const POSITIVE_RULES = [
  TransactionAmountRule.POSITIVE,
  TransactionAmountRule.MINZERO,
]
export const MAX_STRING_LENGTH = 255

const REQUIRED_MESSAGE = 'Field is required'
const LONG_STRING_MESSAGE = 'Too long string'
const MUST_BE_NUMERIC_MESSAGE = 'Must be numeric'
const MUST_BE_EQUAL_ONE_MESSAGE = 'Must be equal one'
const MUST_BE_NEGATIVE_MESSAGE = 'Must be negative'
const MUST_BE_POSITIVE_MESSAGE = 'Must be positive'

export type Rule = yup.AnySchema | yup.AnyObjectSchema
export type RuleWithDependency = (values: any, nullable?: boolean) => Rule

export const rules: Record<string, Rule> = {
  date: yup
    .date()
    .nullable()
    .transform(value => (value === '' ? null : value))
    .test(value => {
      if (!value) return true
      return /^\d{4}-\d{2}-\d{2}$/.test(value.toISOString().split('T')[0])
    })
    .typeError('Please enter a valid date'),
  required: yup
    .string()
    .max(MAX_STRING_LENGTH, LONG_STRING_MESSAGE)
    .trim()
    .required(REQUIRED_MESSAGE),
  limited: yup.string().max(MAX_STRING_LENGTH, LONG_STRING_MESSAGE),
  number: yup.number().typeError(MUST_BE_NUMERIC_MESSAGE),
  positive: yup
    .number()
    .typeError(MUST_BE_NUMERIC_MESSAGE)
    .positive(MUST_BE_POSITIVE_MESSAGE),
  negative: yup
    .number()
    .typeError(MUST_BE_NUMERIC_MESSAGE)
    .negative(MUST_BE_NEGATIVE_MESSAGE),
  minzero: yup
    .number()
    .typeError(MUST_BE_NUMERIC_MESSAGE)
    .min(0, MUST_BE_POSITIVE_MESSAGE),
  maxzero: yup
    .number()
    .typeError(MUST_BE_NUMERIC_MESSAGE)
    .max(0, MUST_BE_NEGATIVE_MESSAGE),
  equalone: yup
    .number()
    .min(1, MUST_BE_EQUAL_ONE_MESSAGE)
    .max(1, MUST_BE_EQUAL_ONE_MESSAGE)
    .typeError(MUST_BE_EQUAL_ONE_MESSAGE),
  percent: yup
    .number()
    .min(-1, 'Must be greater than or equal -100%')
    .notOneOf([0], 'Cannot be zero')
    .max(1, 'Must be less than or equal 100%'),
  accountTags: yup
    .array()
    .of(yup.string())
    .test('Tags', 'Category and Subcategory are required', (item: any) => {
      return item?.filter((item: any) => !!item).length === 2
    }),
}

export const dependencyRules: Record<string, RuleWithDependency> = {
  oneOf: (values: string[], nullable = false) => {
    const chain = yup.string().oneOf([...values, ...(nullable ? [null] : [])], `Must be one of: ${values.join(', ')}`)
    if (nullable) {
      return chain.nullable()
    }
    return chain
  },
  equal: (value: any, nullable = false) => {
    let chain: any
    if (typeof value === 'number') {
      chain = yup.number().equals([value], `Must be equal to ${value}`)
    } else {
      chain = yup.string().equals([value], `Must be equal to ${value}`)
    }
    if (nullable) {
      return chain.nullable()
    }
    return chain
  },
}

export const makeRequired = (rule: Rule) =>
  rule.required(REQUIRED_MESSAGE).nullable()

export const oneof = (
  values: string[],
  types: string[],
  optional = false,
  itemType = 'type',
) => {
  let chain
  if (!optional) {
    chain = yup.string().required(REQUIRED_MESSAGE)
  } else {
    chain = yup.string().nullable()
  }
  return chain
    .max(MAX_STRING_LENGTH, LONG_STRING_MESSAGE)
    .trim()
    .test(
      'is-valid-value',
      `The ${itemType} must be${
        types.length > 1 ? ' one of' : ''
      }: ${types.join(', ')}`,
      value => (value ? values.includes(value) : optional || false),
    )
}
